import { AutoComplete, AutoCompleteChangeEvent, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
// import { Button } from 'primereact/button';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { DropdownItemModel } from '../ExitSlip/ExitSlipModalFilters';
import { Class } from '../../models/Class';
import { availableOptionsInitialState, AvailablesOptions, FormState, SearchItemsType } from '../../pages/SingleUnit';
import { sortClassesList } from '../../utils/sortClassesList';
import { formatClassDisplayName } from '../../utils/formatClassDisplayName';
import { useAppDispatch } from '../../redux/hooks';
import { setTargetGroup } from '../../redux/reducers/targetGroupSlice';
import { setUnit } from '../../redux/reducers/unitSlice';

interface Props {
  miamiBrand: boolean;
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  availablesOptions: AvailablesOptions;
  optionsList: AvailablesOptions;
}

export interface FiltteredOptions {
  roadmap: DropdownItemModel[];
  unit: DropdownItemModel[];
  lesson: DropdownItemModel[]; // optional
  class: Class[];
}

const SingleUnitFilter: FC<Props> = ({ miamiBrand, form, setForm, availablesOptions, optionsList }) => {
  const [filteredOptions, setFilteredOptions] = useState<FiltteredOptions>(availableOptionsInitialState);
  const dispatch = useAppDispatch();

  const handleChange = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // For Roadmap
  const handleRoadmapChange = (value: DropdownItemModel) => {
    handleChange('roadmap', value);
    setForm(prev => ({ ...prev, unitEnabled: !!value?.value, classEnabled: false }));
    dispatch(setTargetGroup(value?.value ? { id: value.value, name: value.label } : undefined));
  };

  // For Unit
  const handleUnitChange = (value: DropdownItemModel) => {
    handleChange('unit', value);
    setForm(prev => ({ ...prev, classEnabled: !!value?.value }));
    dispatch(setUnit(value?.value ? { id: value.value, name: value.label } : undefined));
  };

  const searchItems = (event: AutoCompleteCompleteEvent, itemList: DropdownItemModel[], type: SearchItemsType) => {
    let _filteredItems;
    if (!event.query.trim().length) {
      _filteredItems = [...itemList];
    } else {
      _filteredItems = itemList.filter((x: DropdownItemModel) => {
        return x.label.toLowerCase().includes(event.query.toLowerCase());
      });
    }

    if (type === SearchItemsType.ROADMAP) setFilteredOptions({ ...filteredOptions, roadmap: _filteredItems });
    if (type === SearchItemsType.UNIT) setFilteredOptions({ ...filteredOptions, unit: _filteredItems });
  };
  const classItemTemplate = (cls: Class) => (cls ? formatClassDisplayName(cls) : '');

  return (
    <div className="col-12 flex space-between flex-row tw-overflow-x-auto" id="unitSelector">
      <div className={`p-inputgroup tw-h-min ${miamiBrand ? ' col-3' : ' col-4'}`} style={{ minWidth: 'auto' }}>
        <span className="p-inputgroup-addon" style={{ minWidth: 'auto' }}>
          Roadmap
        </span>
        <AutoComplete
          dropdown
          placeholder="Select a Roadmap"
          value={form.roadmap}
          suggestions={filteredOptions.roadmap}
          completeMethod={e => searchItems(e, optionsList.roadmap, SearchItemsType.ROADMAP)}
          onChange={(e: AutoCompleteChangeEvent) => handleRoadmapChange(e.value)}
          onSelect={() => {
            setForm(prev => ({ ...prev, unitEnabled: true, classEnabled: false, lessonEnabled: false }));
          }}
          field="label"
        />
      </div>
      <div
        className={
          'p-inputgroup tw-h-min' + (miamiBrand ? ' col-3' : ' col-4') + (form.unitEnabled ? '' : ' p-disabled')
        }
        style={{ minWidth: 'auto' }}
      >
        <span className="p-inputgroup-addon" style={{ minWidth: 'auto' }}>
          Unit
        </span>
        <AutoComplete
          dropdown
          placeholder={form.unitEnabled ? 'Select a Unit' : 'Start by choosing a Roadmap'}
          value={form.unit}
          suggestions={filteredOptions.unit}
          completeMethod={e => searchItems(e, availablesOptions.unit, SearchItemsType.UNIT)}
          onChange={(e: AutoCompleteChangeEvent) => handleUnitChange(e.value)}
          onSelect={() => {
            setForm(prev => (miamiBrand ? { ...prev, lessonEnabled: true } : { ...prev, classEnabled: true }));
          }}
          field="label"
        />
      </div>
      {miamiBrand && (
        <div
          className={
            'p-inputgroup tw-h-min ' + (miamiBrand ? ' col-3' : ' col-4') + (form.lessonEnabled ? '' : ' p-disabled')
          }
          style={{ minWidth: 'auto' }}
        >
          <span className="p-inputgroup-addon" style={{ minWidth: 'auto' }}>
            Lessons
          </span>
          <MultiSelect
            onChange={(e: MultiSelectChangeEvent) => setForm({ ...form, lesson: e.value })}
            value={form.lesson}
            options={optionsList.lesson}
            optionLabel="label"
            display="chip"
            id="lessons"
            filter
            className="nc_unit-lessons-autocomplete"
            placeholder={form.lessonEnabled ? 'Select a Lesson' : 'Start by choosing a Unit'}
          />
        </div>
      )}
      <div
        className={
          'p-inputgroup tw-h-min ' +
          (miamiBrand ? 'col-3' : 'col-4') +
          (form.unitEnabled && form.classEnabled ? '' : ' p-disabled')
        }
        style={{ minWidth: 'auto' }}
      >
        <span className="p-inputgroup-addon" style={{ minWidth: 'auto' }}>
          Class(es)
        </span>
        <MultiSelect
          onChange={(e: MultiSelectChangeEvent) => setForm({ ...form, class: e.value })}
          value={form.class}
          options={sortClassesList(optionsList.class)}
          optionLabel="sectionName"
          display="chip"
          id="class"
          selectionLimit={1}
          filter
          className="nc_unit-lessons-autocomplete"
          placeholder={form.class ? 'Select a Class' : 'Start by choosing a Unit'}
          itemTemplate={classItemTemplate}
          selectedItemTemplate={classItemTemplate}
        />
      </div>
    </div>
  );
};

export default SingleUnitFilter;
